/**
 * Microphone access probing and error classification for browser voice calls.
 *
 * The Twilio Voice SDK only requests the microphone when a call starts
 * (device.connect / call.accept), so a blocked microphone surfaces as an
 * opaque call failure. Probing getUserMedia first lets the UI explain the
 * problem — and how to fix it — before any call is reserved or accepted.
 */

export type MicrophoneIssue = 'permission-denied' | 'device-unavailable';

export type MicrophoneAccessResult = { ok: true } | { ok: false; issue: MicrophoneIssue };

// Twilio Voice SDK error codes raised when input audio can't be acquired.
// 31401 = UserMedia Permission Denied, 31402 = UserMedia Acquisition Failed.
const TWILIO_PERMISSION_DENIED_CODE = 31401;
const TWILIO_ACQUISITION_FAILED_CODE = 31402;

// getUserMedia rejection names meaning access was blocked by the user, the
// browser, or the OS. 'PermissionDeniedError' is the legacy Chrome name and
// also the name of Twilio's own wrapper error.
const PERMISSION_ERROR_NAMES = new Set([
  'NotAllowedError',
  'PermissionDeniedError',
  'SecurityError',
]);

// getUserMedia rejection names meaning permission was fine but no usable
// microphone could be started (missing, busy, or overconstrained device).
const DEVICE_ERROR_NAMES = new Set([
  'NotFoundError',
  'DevicesNotFoundError',
  'NotReadableError',
  'TrackStartError',
  'OverconstrainedError',
  'AbortError',
]);

/**
 * Classify an error from getUserMedia or the Twilio Voice SDK. Returns null
 * when the error is not a microphone problem so callers keep their existing
 * handling for unrelated failures.
 */
export const classifyMicrophoneError = (error: unknown): MicrophoneIssue | null => {
  if (!error || typeof error !== 'object') return null;

  const { code, name } = error as { code?: unknown; name?: unknown };

  if (code === TWILIO_PERMISSION_DENIED_CODE) return 'permission-denied';
  if (code === TWILIO_ACQUISITION_FAILED_CODE) return 'device-unavailable';

  if (typeof name === 'string') {
    if (PERMISSION_ERROR_NAMES.has(name)) return 'permission-denied';
    if (DEVICE_ERROR_NAMES.has(name)) return 'device-unavailable';
  }

  return null;
};

// Minimal structural types so the probe can be exercised in Node tests
// without DOM globals. navigator.mediaDevices satisfies AudioMediaDevices.
type StoppableTrack = { stop: () => void };
type AudioStream = { getTracks: () => StoppableTrack[] };
export type AudioMediaDevices = {
  getUserMedia: (constraints: { audio: boolean }) => Promise<AudioStream>;
};

const defaultMediaDevices = (): AudioMediaDevices | undefined =>
  typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;

/**
 * Request a short-lived audio stream to confirm microphone access, stopping
 * the temporary tracks immediately so the browser's mic indicator does not
 * linger. May show the browser's native permission prompt, so only call it
 * from a user gesture (starting or answering a call).
 */
export const probeMicrophoneAccess = async (
  mediaDevices: AudioMediaDevices | undefined = defaultMediaDevices(),
): Promise<MicrophoneAccessResult> => {
  if (!mediaDevices?.getUserMedia) {
    // No mediaDevices API: insecure (non-HTTPS) context or an unsupported
    // browser. Either way the microphone cannot be acquired.
    return { ok: false, issue: 'device-unavailable' };
  }

  try {
    const stream = await mediaDevices.getUserMedia({ audio: true });
    for (const track of stream.getTracks()) {
      track.stop();
    }
    return { ok: true };
  } catch (error) {
    // The probe asked only for `audio: true`, so any rejection here is a
    // microphone problem; bucket unknown rejections as device trouble.
    return {
      ok: false,
      issue: classifyMicrophoneError(error) ?? 'device-unavailable',
    };
  }
};
